/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { debounceTask } from 'ember-lifeline';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { isBefore } from 'date-fns/isBefore';
import { type IDropdown } from 'ember-bootstrap/components/bs-dropdown';
import StayModel from 'uplisting-frontend/models/stay';
import PropertyModel from 'uplisting-frontend/models/property';
import PropertyTagModel from 'uplisting-frontend/models/property-tag';
import { searchOptions, toggleValue } from 'uplisting-frontend/utils';
import { pluralize } from 'ember-inflector';
import { CookieKey } from 'uplisting-frontend/utils/cookies';
import PlaceholderComponent from 'uplisting-frontend/components/ui/occasion-search-bar-placeholder';

interface IArgs {
  // eslint-disable-next-line no-use-before-define
  onInit(component: UiSearchHeaderComponent): void;
  onLoadData(): Promise<void>;
}

interface SearchHeaderSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiSearchHeaderComponent extends Component<SearchHeaderSignature> {
  @service router!: Services['router'];
  @service cookies!: Services['cookies'];

  @service('repositories/stay')
  stayRepository!: Services['repositories/stay'];

  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  @service('repositories/property-tag')
  propertyTagRepository!: Services['repositories/property-tag'];

  @cached @tracked inputValue = '';
  @cached @tracked selectedItems: (PropertyModel | PropertyTagModel)[] = [];

  constructor(owner: Owner, args: IArgs) {
    super(owner, args);

    this.args.onInit(this);
    this.setPropertiesConfig();
  }

  get placeholderComponent(): typeof PlaceholderComponent {
    return PlaceholderComponent;
  }

  @cached
  get properties(): PropertyModel[] {
    return this.propertyRepository.peekAll();
  }

  @cached
  get filteredProperties(): PropertyModel[] {
    return searchOptions<PropertyModel>(
      this.properties,
      this.inputValue,
      'searchName',
    );
  }

  @cached
  get propertyTags(): PropertyTagModel[] {
    return this.propertyTagRepository
      .peekAll()
      .filter((tag) => tag.properties.length);
  }

  @cached
  get filteredPropertyTags(): PropertyTagModel[] {
    return searchOptions<PropertyTagModel>(
      this.propertyTags,
      this.inputValue,
      'searchName',
    );
  }

  @cached
  get noDataMatches(): boolean {
    return !this.filteredProperties.length && !this.filteredPropertyTags.length;
  }

  @action
  findStays(query: string) {
    return new Promise((resolve) => {
      debounceTask(this, 'fetchStays', query, resolve, 600);
    });
  }

  @action
  onInput(text: string): boolean {
    return text.length > 2;
  }

  @action
  handleInputSearch(value: string): void {
    this.inputValue = value;
  }

  @action
  handleClosePropertiesSelectorFilter(): void {
    this.inputValue = '';
  }

  @action
  async handleSelectStay(stay: StayModel): Promise<void> {
    const id = stay.id.split('-')[1] as string;
    const routeType = stay.type.replace(/_/g, '-');

    await this.router.transitionTo(
      `action.${pluralize(routeType)}.summary`,
      id,
    );
  }

  @action
  async handleItemClick(item: PropertyModel | PropertyTagModel): Promise<void> {
    toggleValue(this, 'selectedItems', item);

    this.writeSelectedItems();

    await this.args.onLoadData();
  }

  @action
  async handleUnselectAllProperties(dd: IDropdown): Promise<void> {
    this.selectedItems = [];
    this.writeSelectedItems();

    dd.closeDropdown();

    await this.args.onLoadData();
  }

  private async fetchStays(query: string, resolve) {
    const stays = await this.stayRepository.query({ query });

    const sorted = (stays.slice() as StayModel[]).sort((x, b) => {
      if (isBefore(x.checkOut, b.checkOut)) {
        return -1;
      } else if (isBefore(b.checkOut, x.checkOut)) {
        return 1;
      }

      return 0;
    });

    const ongoing = sorted.filter(
      (item) => item.isOngoing && !item.isCancelled,
    );
    const past = sorted
      .filter((item) => item.isInPast && !item.isCancelled)
      .reverse();
    const future = sorted.filter(
      (item) => item.isInFuture && !item.isCancelled,
    );
    const cancelled = sorted.filter((item) => item.isCancelled);

    if (past.length) {
      (past[0] as StayModel).pastDelimiter = true;
    }

    if (cancelled.length) {
      (cancelled[0] as StayModel).cancelledDelimiter = true;
    }

    return resolve([...ongoing, ...future, ...past, ...cancelled]);
  }

  private writeSelectedItems(): void {
    const { selectedItems } = this;

    const data = {
      propertyIds: selectedItems
        .filter((item) => item instanceof PropertyModel)
        .map((item) => item.id),

      propertyTagIds: selectedItems
        .filter((item) => item instanceof PropertyTagModel)
        .map((item) => item.id),
    };

    this.cookies.write(CookieKey.ACTION_PROPERTIES, JSON.stringify(data));
  }

  private setPropertiesConfig(): void {
    const data = this.cookies.read<string>(CookieKey.ACTION_PROPERTIES);

    if (!data) {
      return;
    }

    const object = JSON.parse(data);

    const { properties, propertyTags } = this;

    object.propertyIds.forEach((id) => {
      const item = properties.find((property) => property.id === id);

      if (!item) {
        return;
      }

      this.selectedItems.push(item);
    });

    object.propertyTagIds.forEach((id) => {
      const item = propertyTags.find((tag) => tag.id === id);

      if (!item) {
        return;
      }

      this.selectedItems.push(item);
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Search::Header': typeof UiSearchHeaderComponent;
  }
}
